import { useState } from "react";

import type { AuthResult } from "../../hooks/useAuth";

type AuthPageProps = {
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signUp: (email: string, password: string) => Promise<AuthResult>;
};

const authErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return "Nao foi possivel autenticar. Verifique os dados e tente novamente.";
};

export const AuthPage = ({ loading, signIn, signUp }: AuthPageProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submittingAction, setSubmittingAction] = useState<
    "signIn" | "signUp" | null
  >(null);

  const isSubmitting = loading || submittingAction !== null;

  const validate = () => {
    if (!email.trim()) {
      return "Informe seu email.";
    }

    if (!password) {
      return "Informe sua senha.";
    }

    if (password.length < 6) {
      return "A senha precisa ter pelo menos 6 caracteres.";
    }

    return "";
  };

  const handleSignIn = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    const validationError = validate();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    console.log("[Auth] Tentando login", email);
    setSubmittingAction("signIn");

    try {
      const { error } = await signIn(email.trim(), password);

      if (error) {
        console.error("[Auth] Erro", error);
        setErrorMessage(authErrorMessage(error));
      }
    } catch (error) {
      console.error("[Auth] Erro", error);
      setErrorMessage(authErrorMessage(error));
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleSignUp = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    const validationError = validate();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    console.log("[Auth] Tentando criar conta", email);
    setSubmittingAction("signUp");

    try {
      const { data, error } = await signUp(email.trim(), password);

      if (error) {
        console.error("[Auth] Erro", error);
        setErrorMessage(authErrorMessage(error));
        return;
      }

      setSuccessMessage(
        data.session
          ? "Conta criada. Voce ja esta conectado."
          : "Conta criada. Verifique seu email para confirmar o cadastro.",
      );
    } catch (error) {
      console.error("[Auth] Erro", error);
      setErrorMessage(authErrorMessage(error));
    } finally {
      setSubmittingAction(null);
    }
  };

  return (
    <main
      style={{
        position: "relative",
        zIndex: 1,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "#f8f7ff",
        color: "#1f1f29",
        fontFamily:
          "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <section
        style={{
          width: "100%",
          maxWidth: 360,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          padding: 24,
          border: "1px solid #dedbed",
          borderRadius: 8,
          background: "#ffffff",
          boxShadow: "0 8px 28px rgba(31, 31, 41, 0.08)",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22, lineHeight: 1.2 }}>Draw</h1>
          <p style={{ margin: "8px 0 0", color: "#696678", fontSize: 14 }}>
            Entre para acessar seus boards.
          </p>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 13, color: "#4f4b61" }}>Email</span>
          <input
            autoComplete="email"
            disabled={isSubmitting}
            onChange={(event) => setEmail(event.target.value)}
            style={{
              height: 40,
              border: "1px solid #cbc7da",
              borderRadius: 6,
              padding: "0 10px",
              fontSize: 15,
            }}
            type="email"
            value={email}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 13, color: "#4f4b61" }}>Senha</span>
          <input
            autoComplete="current-password"
            disabled={isSubmitting}
            onChange={(event) => setPassword(event.target.value)}
            style={{
              height: 40,
              border: "1px solid #cbc7da",
              borderRadius: 6,
              padding: "0 10px",
              fontSize: 15,
            }}
            type="password"
            value={password}
          />
        </label>

        {errorMessage && (
          <div
            role="alert"
            style={{
              padding: "10px 12px",
              border: "1px solid #ffd0d0",
              borderRadius: 6,
              background: "#fff5f5",
              color: "#9f2525",
              fontSize: 13,
            }}
          >
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div
            role="status"
            style={{
              padding: "10px 12px",
              border: "1px solid #bfe8cb",
              borderRadius: 6,
              background: "#f1fff5",
              color: "#1f6b37",
              fontSize: 13,
            }}
          >
            {successMessage}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button
            disabled={isSubmitting}
            onClick={handleSignIn}
            style={{
              flex: 1,
              height: 40,
              border: 0,
              borderRadius: 6,
              background: "#6965db",
              color: "#ffffff",
              cursor: isSubmitting ? "default" : "pointer",
              fontWeight: 600,
            }}
            type="button"
          >
            {submittingAction === "signIn" ? "Entrando..." : "Entrar"}
          </button>
          <button
            disabled={isSubmitting}
            onClick={handleSignUp}
            style={{
              flex: 1,
              height: 40,
              border: "1px solid #cbc7da",
              borderRadius: 6,
              background: "#ffffff",
              color: "#2f2a45",
              cursor: isSubmitting ? "default" : "pointer",
              fontWeight: 600,
            }}
            type="button"
          >
            {submittingAction === "signUp" ? "Criando..." : "Criar conta"}
          </button>
        </div>
      </section>
    </main>
  );
};
